import classNames from 'classnames';
import { ReactNode } from 'react';
import { CmsImage } from '../CmsImage';
import { CC_CARD_TEXT } from './utils';

type CcProductCardIdentityProps = {
  imageSrc: string | undefined;
  productName: string | undefined;
  providerName: string | undefined;
  shortDescription: string | undefined;
  /** Under the title: the product's Google rating, as on ProductCard. */
  rating?: ReactNode;
  /** At the top right, clear of the badge band: the Compare control (BRD-02 FR-2.2.1). */
  aside?: ReactNode;
};

/** Card image, provider eyebrow, product title and the one-line short description (FR-1.3). */
export const CcProductCardIdentity: React.FC<CcProductCardIdentityProps> = ({
  imageSrc,
  productName,
  providerName,
  shortDescription,
  rating,
  aside,
}) => (
  <div
    data-component="cc-product-card-identity"
    // Top-aligned, so the title sits at the same height on every card, whatever follows it.
    className="flex items-start gap-3.5"
  >
    {/* Fixed box at the prototype's size on every breakpoint (92×58): the image never shifts the
        layout, and a missing one leaves a neutral fill. */}
    <div
      className={classNames(
        'h-14 w-24 shrink-0 overflow-hidden rounded-radius/tooltip',
        { 'bg-fill/alternative-1': !imageSrc },
      )}
    >
      {imageSrc && (
        <CmsImage
          src={imageSrc}
          alt={productName ?? ''}
          width={96}
          height={56}
          sizes={{ _: 96 }}
          className="size-full object-contain"
        />
      )}
    </div>
    <div className="flex min-w-0 grow flex-col gap-spacing/gap/soft-4px">
      {/* The provider's line holds its height when there is none, so the title does not move up. */}
      <span
        className={classNames(
          CC_CARD_TEXT.label,
          'line-clamp-1 min-h-4 text-text/secondary-lighter',
        )}
      >
        {providerName}
      </span>
      <h3
        className={classNames(
          CC_CARD_TEXT.title,
          'line-clamp-2 text-text/primary',
        )}
      >
        {productName}
      </h3>
      {rating}
      {shortDescription && (
        <p
          className={classNames(
            CC_CARD_TEXT.note,
            'truncate text-text/secondary-lighter',
          )}
        >
          {shortDescription}
        </p>
      )}
    </div>
    {aside}
  </div>
);
